/**
 * THE ONE WAY the dashboard writes a date, a time, a month and a weekday.
 *
 * | Kind              | Example               |
 * |-------------------|-----------------------|
 * | date              | 01/10/2026            |
 * | date and time     | 01/10/2026, 2:05 PM   |
 * | time              | 2:05 PM               |
 * | weekday and date  | الخميس 01/10/2026     |
 * | month             | 10/2026               |
 *
 * THE RULES (the owner, 2026-10-01): the day comes first; a date is numbers
 * only, with no month name; the clock has 12 hours with AM / PM and no seconds;
 * the digits are Western. The weekday is the only word, because a weekday has
 * no number form.
 *
 * WHY ONE. `toLocaleString()` with no locale follows the browser. An English
 * browser wrote 1 October as «10/1/2026», which an operator here reads as
 * 10 January, so a list of orders looked out of order. Other screens used
 * `ar-EG`, `en-GB` or their own code, so one moment read differently on two
 * screens.
 *
 * So: do not call `toLocaleString()` on a date in a component, and do not use
 * a browser date or time input. Use these functions, `Ui/DateTimeField` and
 * `Ui/TimeField`. The text is left-to-right: in an Arabic line, wrap it in
 * `<bdi dir="ltr">`; inside a plain string, use `ltrText`.
 *
 * Nothing here uses the browser's locale. The text is built from the numbers,
 * so it is the same on every computer.
 *
 * Every function returns null for an empty or invalid value, so the caller
 * chooses its own placeholder: `formatDateTime(value) ?? "—"`.
 *
 * @typedef {string|number|Date|null|undefined} DateInput
 * @typedef {{year: number, month: number, day: number, hour: number|null, minute: number|null}} DateParts
 */

/** The API's day names (`vendor_working_hours.day`), in `Date#getDay` order. */
export const WEEKDAYS = Object.freeze([
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
]);

/**
 * The Arabic name of each weekday. The one list: the hours forms, the shift
 * pages and the date picker all read it.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const WEEKDAY_LABELS = Object.freeze({
    sunday: "الأحد",
    monday: "الإثنين",
    tuesday: "الثلاثاء",
    wednesday: "الأربعاء",
    thursday: "الخميس",
    friday: "الجمعة",
    saturday: "السبت",
});

/** `[{ key, label }]` in week order from Sunday, for a form with a row per day. */
export const WEEKDAY_OPTIONS = Object.freeze(
    WEEKDAYS.map((key) => Object.freeze({ key, label: WEEKDAY_LABELS[key] })),
);

const pad = (number) => String(number).padStart(2, "0");

// A WALL-CLOCK STRING: "YYYY-MM-DD", with or without "HH:mm[:ss]", and with no
// zone. `orders.createdAt` filters, shift dates and offer dates have this
// shape. It is read as written. `new Date("2026-10-01")` would read it as UTC
// and could show the day before.
const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?)?$/;

/**
 * @param {DateInput} value
 * @returns {DateParts|null}
 */
const toParts = (value) => {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value === "string") {
        const match = WALL_CLOCK.exec(value.trim());
        if (match) {
            return {
                year: Number(match[1]),
                month: Number(match[2]),
                day: Number(match[3]),
                hour: match[4] === undefined ? null : Number(match[4]),
                minute: match[5] === undefined ? null : Number(match[5]),
            };
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return {
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
    };
};

const datePart = (parts) => `${pad(parts.day)}/${pad(parts.month)}/${parts.year}`;

const timePart = (hour, minute) => `${hour % 12 || 12}:${pad(minute)} ${hour < 12 ? "AM" : "PM"}`;

/**
 * A date with no time: «01/10/2026».
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatDate = (value) => {
    const parts = toParts(value);
    return parts ? datePart(parts) : null;
};

/**
 * A date and a time: «01/10/2026, 2:05 PM». A value with no time part gives
 * the date alone.
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatDateTime = (value) => {
    const parts = toParts(value);
    if (!parts) return null;
    if (parts.hour === null) return datePart(parts);
    return `${datePart(parts)}, ${timePart(parts.hour, parts.minute)}`;
};

/**
 * A time of day: «2:05 PM». Takes a Date, an instant, or the "HH:mm" /
 * "HH:mm:ss" string of a schedule (`openTime`, `start_time`).
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatTime = (value) => {
    if (typeof value === "string") {
        const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
        if (match) {
            const hour = Number(match[1]);
            const minute = Number(match[2]);
            return hour < 24 && minute < 60 ? timePart(hour, minute) : null;
        }
    }
    const parts = toParts(value);
    return parts && parts.hour !== null ? timePart(parts.hour, parts.minute) : null;
};

/**
 * The API's day name of a date: "thursday".
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const weekdayOf = (value) => {
    const parts = toParts(value);
    if (!parts) return null;
    return WEEKDAYS[new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay()];
};

/**
 * The Arabic weekday of a date: «الخميس».
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatWeekday = (value) => WEEKDAY_LABELS[weekdayOf(value)] ?? null;

/**
 * A weekday and a date: «الخميس 01/10/2026». With a time in the value:
 * «الخميس 01/10/2026, 8:00 PM».
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatWeekdayDate = (value) => {
    const weekday = formatWeekday(value);
    return weekday ? `${weekday} ${formatDateTime(value)}` : null;
};

/**
 * A month: «10/2026». Takes "YYYY-MM", or any date in that month.
 *
 * @param {DateInput} value
 * @returns {string|null}
 */
export const formatMonth = (value) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(value ?? "").trim());
    if (match) {
        const month = Number(match[2]);
        return month >= 1 && month <= 12 ? `${match[2]}/${match[1]}` : null;
    }
    const parts = toParts(value);
    return parts ? `${pad(parts.month)}/${parts.year}` : null;
};

/**
 * Keeps a date or a time in order INSIDE AN ARABIC STRING, such as a tooltip
 * or a note, where a `<bdi dir="ltr">` cannot go. It wraps the text in the
 * Unicode marks «left-to-right isolate» and «pop isolate». Without them the
 * browser moves «PM» and the comma to the wrong side of the date.
 *
 * In JSX, use `<bdi dir="ltr">` and not this.
 *
 * @param {string|null} text
 * @returns {string|null}
 */
export const ltrText = (text) => (text ? `\u2066${text}\u2069` : text);
